import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibCautionOutlineFillIcon } from '@chewy/kib-icons-react'
import { Grid, styled, Tooltip } from '@mui/material'
import { Utils } from '@pbt/pbt-ui-components'

import Typography from '~/components/elements/Typography/Typography'
import FeatureToggle from '~/constants/featureToggle'
import { getDocumentTypes, getFeatureToggle } from '~/store/reducers/constants'
import type { Document } from '~/types'

const Subtext = styled(Typography.Paragraph)`
  color: ${({ theme }) => theme.colors.grayGray2};
  padding-left: ${({ theme }) => theme.spacing(0.5)};
`

const WarningIconContainer = styled('div')`
  display: flex;
  align-items: baseline;
  margin-right: ${({ theme }) => theme.spacing(0.5)};
  color: ${({ theme }) => theme.colors.uiWarning};
  padding-top: 2px;

  svg {
    width: 20px;
    height: 20px;
  }
`

const DocumentNameContainer = styled(Grid)`
  white-space: nowrap;
  flex-wrap: nowrap;
`

const DocumentName = styled(Typography.ParagraphLg)`
  text-overflow: ellipsis;
  overflow: hidden;
`

const DocumentNameCell = (document: Document) => {
  const { t } = useTranslation('Common')
  const DocumentTypes = useSelector(getDocumentTypes)

  const isLegacyFormWarningEnabled = useSelector(
    getFeatureToggle(FeatureToggle.LEGACY_FORM_WARNING),
  )

  const medicalNotesType = Utils.findConstantIdByName(
    'Medical notes template',
    DocumentTypes,
  )
  const dischargeNotesType = Utils.findConstantIdByName(
    'Discharge notes template',
    DocumentTypes,
  )

  // Use the backend flag to determine if this is a legacy form
  // Backend already ensures legacyForm is only true for Form documents that are actually legacy
  const isLegacy = document.legacyForm ?? false
  const isLegacyTemplate = isLegacyFormWarningEnabled && isLegacy

  let defaultMessage = ''
  if (document.type === medicalNotesType) {
    defaultMessage = `(${t('Common:SET_AS_DEFAULT_MEDICAL_NOTES_TEMPLATE')})`
  } else if (document.type === dischargeNotesType) {
    defaultMessage = `(${t('Common:SET_AS_DEFAULT_DISCHARGE_NOTES_TEMPLATE')})`
  }

  const renderWarningIcon = () => {
    if (!isLegacyTemplate) return null

    return (
      <WarningIconContainer data-testid="legacy-warning-icon">
        <Tooltip title={t('Common:LEGACY_FORM_TEMPLATE')}>
          <span>
            <KibCautionOutlineFillIcon />
          </span>
        </Tooltip>
      </WarningIconContainer>
    )
  }

  return (
    <DocumentNameContainer container alignItems="center">
      {renderWarningIcon()}
      <DocumentName>{document.name}</DocumentName>
      {document.defaultTemplate && <Subtext>{defaultMessage}</Subtext>}
    </DocumentNameContainer>
  )
}

export default DocumentNameCell
